import { PropsWithChildren, useEffect, useRef, useState } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { passwordPattern } from "../../../../constants";
import { IAddPatient } from "../../../../models/users.interface";
import {
  PasswordStrength,
  Modal,
  FormWarning,
  Popover,
} from "@shltelemedicine/shl-ui-lib";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import PatientsIcon from "../../../Shared/PatientsIcon/PatientsIcon";
import SaveButton from "../../../Shared/SaveButton/SaveButton";
import TextFormInput from "../../../Shared/TextInput/TextFormInput";
import PatientInfoInputs from "../PatientInfoInputs/PatientInfoInputs";
import PatientOrgInfoInputs from "../PatientOrgInfoInputs/PatientOrgInfoInputs";
import styles from "./AddPatientModal.module.css";
import PatientPermissionsInputs from "../PatientPermissionsInputs/PatientPermissionsInputs";
import PasswordRules from "../../../ChangePassword/PasswordRules/PasswordRules";
import InfoIcon from "../../../Shared/InfoIcon/InfoIcon";
import CheckBoxFormInput from "../../../Shared/CheckBox/CheckBoxFormInput";

type AddPatientModalProps = {
  show?: boolean;
  onClose?: () => void;
  onSave?: (newPatient: IAddPatient) => Promise<void>;
};

const AddPatientModal = ({ show, onClose, onSave }: AddPatientModalProps) => {
  const [saving, setSaving] = useState(false);

  const formMethods = useForm<IAddPatient>({
    defaultValues: {
      beltSize: "M",
      hasPacemaker: "false",
      isOneTimePassword: true,
    },
  });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors: formErrors },
  } = formMethods;

  const passwordRef = useRef<string>("");
  passwordRef.current = watch("password", "") ?? "";

  useEffect(() => {
    if (show === true) {
      reset();
    }
  }, [show, reset]);

  const saveHandler = async (newPatient: IAddPatient) => {
    if (!onSave) return;
    setSaving(true);
    await onSave(newPatient);
    setSaving(false);
  };

  return (
    <Modal
      dialogClassName={styles.dialog}
      centered
      backdrop="static"
      show={show}
      onHide={onClose}
      header={
        <div className="d-flex align-items-center">
          <PatientsIcon />
          <span className="mx-1"></span>
          <p className={`${styles.title} mb-0`}>
            <FormattedMessage
              id="MANAGEMENT.ADD_PATIENT"
              defaultMessage="Add patient"
            />
          </p>
        </div>
      }
      footer={
        <div className="d-flex align-items-center justify-content-end border-top pt-2 px-3 m-0">
          <CancelButton onClick={onClose} disabled={saving} />
          <span className="mx-2"></span>
          <SaveButton
            onClick={handleSubmit(saveHandler)}
            className="prim-bg-gradient-color capitalized"
            disabled={saving}
          >
            {saving && (
              <>
                <FormattedMessage id="GLOBAL.SAVE" defaultMessage="Save" />
                &nbsp;&nbsp;
                <Spinner as="span" animation="border" size="sm" />
              </>
            )}
          </SaveButton>
        </div>
      }
    >
      <FormProvider {...formMethods}>
        <Form className="py-4 px-4">
          <Container divide>
            <PatientOrgInfoInputs disabled={saving} />
          </Container>
          <Container divide>
            <PatientInfoInputs disabled={saving} />
          </Container>
          <Container divide>
            <PatientPermissionsInputs disabled={saving} />
          </Container>
          <Container>
            <p className="mb-2">
              <FormattedMessage
                id="MANAGEMENT.ADD_PATIENT.PASSWORD_TITLE"
                defaultMessage="Add initial password, make it strong"
              />
              <Popover
                id="passwordRules"
                hoverContent={<PasswordRules />}
                placement="top"
                className={styles.popover}
              >
                <InfoIcon className={styles.infoIcon} />
              </Popover>
            </p>
            <Form.Group as={Row} className="align-items-center mb-2">
              <Form.Label column className="font-md">
                <span className="prim-color">*</span>
                <span className="mx-sm">
                  <FormattedMessage
                    id="LOGIN.PASSWORD"
                    defaultMessage="Password"
                  />
                </span>
              </Form.Label>
              <Col sm={6}>
                <TextFormInput
                  name={"password"}
                  control={control}
                  rules={{
                    required: true,
                    minLength: 8,
                    pattern: passwordPattern,
                  }}
                  input={{
                    type: "password",
                    isInvalid: !!formErrors?.password,
                    disabled: saving,
                  }}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="align-items-center mb-2">
              <Form.Label column className="font-md">
                <span className="prim-color">*</span>
                <span className="mx-sm">
                  <FormattedMessage
                    id="CHANGE_PASSWORD.RETYPE_PASSWORD"
                    defaultMessage="Confirm Password"
                  />
                </span>
              </Form.Label>
              <Col sm={6}>
                <TextFormInput
                  name={"passwordConfirmation"}
                  control={control}
                  rules={{
                    required: true,
                    validate: {
                      passwordMismatch: (value) =>
                        value === passwordRef.current,
                    },
                  }}
                  input={{
                    type: "password",
                    isInvalid: !!formErrors?.passwordConfirmation,
                    disabled: saving,
                  }}
                />
              </Col>
            </Form.Group>
            <Row>
              <Col xs={12}>
                <CheckBoxFormInput
                  name={"isOneTimePassword"}
                  control={control}
                  input={{
                    label: (
                      <FormattedMessage
                        id="CHANGE_PASSWORD.ENABLE_ONE_TIME_PASSWORD"
                        defaultMessage="Enable one time password"
                      />
                    ),
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <PasswordStrength password={passwordRef.current} />
              </Col>
              <Col xs={6}>
                {(!!formErrors?.password ||
                  !!formErrors?.passwordConfirmation) && (
                  <FormWarning
                    className="font-sm flex-grow-1"
                    message={
                      <FormattedMessage
                        id="SETTINGS.VALID_PASSWORD_REQUIRED"
                        defaultMessage="Insert Valid Password"
                      />
                    }
                  />
                )}
              </Col>
            </Row>
          </Container>
        </Form>
      </FormProvider>
    </Modal>
  );
};

export default AddPatientModal;

const Divider = () => <div className="border-bottom my-3"></div>;

const Container = ({
  divide,
  children,
}: PropsWithChildren<{ divide?: boolean }>) => (
  <>
    <div className="px-5">{children}</div>
    {divide && <Divider />}
  </>
);
